'use client'

import { useModal } from '@ebay/nice-modal-react'
import { dispatchBaseModalToast } from '@trustoo/components/modal/baseModalToastStore'
import { createToast } from '@trustoo/components/ui/toaster/funcs/createToast'
import type { ToastAction } from '@trustoo/components/ui/toaster/funcs/toastReducer'
import type { ToastActions } from '@trustoo/components/ui/toaster/types/toastTypes'
import { useEffect, useMemo, useRef } from 'react'

/**
 * Toasts for the current NiceModal's `BaseModal`, callable from anywhere within the NiceModal
 * component, including the component rendering the card. Use the app toast hook for messages
 * that must survive closing the modal.
 */
export const useBaseModalToast = (): ToastActions => {
    const { id: modalId } = useModal()
    const mounted = useRef(true)

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    return useMemo<ToastActions>(() => {
        // A request may settle after the modal was removed and reopened under the same id; the
        // store cannot tell the two instances apart, so the caller's own lifetime decides.
        const send = (action: ToastAction) => {
            if (mounted.current) dispatchBaseModalToast(modalId, action)
        }
        return {
            addToast: (options = {}) => send({ type: 'add', toast: createToast(options) }),
            deleteToast: (id) => send({ type: 'delete', id }),
            deleteAllToasts: () => send({ type: 'deleteAll' }),
        }
    }, [modalId])
}
